import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ComponentRef, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FieldValidation } from '../field-validation';
import { InlineEditService } from '../inline-edit.service';
import { CrmFieldWarningDirective } from './model-driven-form-error/field-warning.directive';
import { FieldWarningService } from './model-driven-form-error/field-warning.service';
import { CrmModelDrivenFormErrorComponent } from './model-driven-form-error/model-driven-form-error.component';
import { CrmModelDrivenPopoverComponent } from './model-driven-popover/model-driven-popover.component';
import { stopChromeAutofill } from './stop-chrome-autofill';
import { FieldValueInterface, ObjectType, RenderableFieldInterface } from '../../../tokens-and-interfaces';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';

import { toSignal } from '@angular/core/rxjs-interop';

export class EmailFieldHandler implements RenderableFieldInterface {
  private component: ComponentRef<EmailRendererComponent> | null = null;
  formControl: UntypedFormControl;

  constructor(
    public objectType: ObjectType,
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string,
    public description: string,
    public readOnly = false,
    public validation: FieldValidation = new FieldValidation(),
  ) {
    this.formControl = new UntypedFormControl(this.value);
  }

  setValidation(validation: FieldValidation): void {
    this.validation = validation;
    this.formControl.setValidators(validation.getValidators());
    this.formControl.setAsyncValidators(validation.getAsyncValidators());
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  fieldValue(): FieldValueInterface {
    return {
      fieldId: this.fieldId,
      stringValue: this.formControl.value,
    };
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<EmailRendererComponent>(EmailRendererComponent);
    component.instance.objectType = this.objectType;
    component.instance.fieldId = this.fieldId;
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.validation = this.validation;
    component.instance.readOnly = this.readOnly;
    this.component = component;
  }
}

@Component({
  template: `
    <div class="crm-read-only-container" *ngIf="readOnly">
      <div class="crm-field-label">
        {{ label | translate }}
      </div>
      <div class="crm-field-value">
        <!-- ng-container necessary since Angular interpolation will add empty space -->
        <ng-container *ngIf="control.value">
          {{ control.value }}
        </ng-container>
      </div>
    </div>
    <div *ngIf="isInlineEdit$ | async">
      <crm-model-driven-popover
        [label]="label"
        [displayValue]="control.value"
        [control]="control"
        [validation]="validation"
        (save)="saveField()"
      >
        <ng-container *ngTemplateOutlet="formField"></ng-container>
      </crm-model-driven-popover>
    </div>
    <ng-container *ngIf="!readOnly && (isInlineEdit$ | async) === false">
      <ng-container *ngTemplateOutlet="formField"></ng-container>
    </ng-container>
    <ng-template #formField>
      <glxy-form-field
        [crmFieldWarning]="validation"
        [warningControl]="control"
        [attr.data-testid]="'field-with-id-' + fieldId"
      >
        <glxy-label *ngIf="label">{{ label | translate }}</glxy-label>
        <input
          matInput
          [formControl]="control"
          [placeholder]="'MODEL_DRIVEN_FORM.ADD_FIELD_VALUE' | translate: { fieldName: label?.toLowerCase() }"
          #textInput="matInput"
          maxlength="4000"
          id="{{ disableAutoComplete }}"
          name="{{ disableAutoComplete }}"
          autocomplete="{{ disableAutoComplete }}"
          (keydown.enter)="$event.preventDefault()"
        />
        @if (control.value) {
          <button matSuffix mat-icon-button class="crm-field-clear-button" aria-label="Clear" (click)="clear()">
            <mat-icon>close</mat-icon>
          </button>
        }
        <glxy-error *ngIf="control.value?.length === 4000">
          {{ 'MODEL_DRIVEN_FORM.STRING_LIMIT_HINT' | translate: { characters: control.value?.length || 0 } }}
        </glxy-error>
        <crm-model-driven-form-error
          [objectType]="objectType"
          [fieldId]="fieldId"
          [label]="label"
          [control]="control"
          [validation]="validation"
        ></crm-model-driven-form-error>
      </glxy-form-field>
    </ng-template>
  `,
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    CrmModelDrivenFormErrorComponent,
    CrmFieldWarningDirective,
    CrmModelDrivenPopoverComponent,
    MatIcon,
    MatIconButton,
  ],
  providers: [FieldWarningService],
  styleUrls: ['./renderers.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailRendererComponent {
  fieldId!: string;
  objectType!: ObjectType;
  control!: UntypedFormControl;
  label!: string;
  readOnly!: boolean;
  validation!: FieldValidation;
  isInlineEdit$ = this.inlineEditService.isInlineEdit$;
  isInlineEdit = toSignal(this.isInlineEdit$);

  disableAutoComplete: string = stopChromeAutofill();

  constructor(private readonly inlineEditService: InlineEditService) {}

  clear(): void {
    this.control.reset();
    this.control.markAsDirty();
    if (this.isInlineEdit()) {
      this.saveField();
    }
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, [
      {
        fieldId: this.fieldId,
        stringValue: this.control.value,
      },
    ]);
  }
}
